import { Fragment } from 'react'

import DynamicIcon, { IconName } from '@/components/DynamicIcon'
import { Kicker } from '@/components/Kicker'
import { CMSLink, CMSLinkType } from '@/components/Link'
import { Media } from '@/components/Media'
import { RevealFade, RevealSection } from '@/components/RevealSection'
import RichText from '@/components/RichText'
import { Button, ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import { PreviewVideoButton } from './PreviewVideoButton'

import type { Page } from '@/payload-types'

// Maps old CMS color values to new button props
type OldColorValue =
  | 'primary'
  | 'secondary'
  | 'outlinePrimary'
  | 'outlineSecondary'
  | 'ghostPrimary'
  | 'ghostSecondary'
  | 'evergreen'
  | 'none'

const mapColorToButtonProps = (
  oldColor: OldColorValue | string | undefined,
): { variant: ButtonProps['variant']; color: ButtonProps['color']; ghost?: boolean } => {
  switch (oldColor) {
    case 'primary':
      return { variant: 'primary', color: 'default' }
    case 'secondary':
      return { variant: 'primary', color: 'inverse' }
    case 'outlinePrimary':
      return { variant: 'outline', color: 'default' }
    case 'outlineSecondary':
      return { variant: 'outline', color: 'inverse' }
    case 'ghostPrimary':
      return { variant: 'primary', color: 'default', ghost: true }
    case 'ghostSecondary':
      return { variant: 'primary', color: 'inverse', ghost: true }
    case 'evergreen':
      return { variant: 'accent', color: 'default' }
    case 'none':
      return { variant: 'link', color: 'default' }
    default:
      return { variant: 'primary', color: 'default' }
  }
}

export const RenderLink = ({
  link,
  color: oldColor,
  variant: explicitVariant,
  icon = true,
  className,
  ...props
}: {
  link: CMSLinkType
  color?: OldColorValue | string
  variant?: ButtonProps['variant']
  icon?: boolean
  className?: string
} & Omit<ButtonProps, 'color' | 'variant'>) => {
  const mapped = mapColorToButtonProps(oldColor)
  // Allow explicit variant to override the mapped one
  const variant = explicitVariant ?? mapped.variant
  const color = mapped.color
  const ghost = mapped.ghost

  if (link.type === 'button') {
    return (
      <Button
        variant={variant}
        color={color}
        ghost={ghost}
        icon={icon}
        className={className}
        {...props}
      >
        {variant === 'icon' ? '' : link.label}
      </Button>
    )
  }
  if (link.type === 'reference' || link.type === 'custom') {
    return (
      <CMSLink
        {...link}
        variant={variant}
        color={color}
        ghost={ghost}
        icon={icon}
        className={className}
      />
    )
  }
  return null
}
const getHeroThemeColor = (heroThemeColor: string) => {
  switch (heroThemeColor) {
    case 'accent-foreground-4':
      return 'bg-accent-foreground-4'
    case 'accent-foreground-2':
      return 'bg-accent-foreground-2'
    case 'accent-foreground-3':
      return 'bg-accent-foreground-3'
    case 'accent-foreground-5':
      return 'bg-accent-foreground-5'
    case 'accent':
      return 'bg-accent'
    default:
      return 'bg-accent-foreground-4'
  }
}

const getHeadingColor = (heroThemeColor: string) => {
  switch (heroThemeColor) {
    case 'accent-foreground-4':
      return 'text-accent-4'
    case 'accent-foreground-2':
      return 'text-accent-2'
    case 'accent-foreground-3':
      return 'text-accent-3'
    case 'accent-foreground-5':
      return 'text-accent-5'
    case 'accent':
      return 'text-accent-foreground'
    default:
      return 'text-accent-4'
  }
}

const CustomIcon = ({ icon }: { icon: string }) => {
  return <DynamicIcon name={icon as IconName} className="size-3" />
}

const getLinkColor = (index: number, heroButtonColor: OldColorValue | string | undefined) =>
  index % 2 === 0 ? heroButtonColor || 'primary' : 'outlinePrimary'

const getAlternateButtonColor = (color: OldColorValue | string | undefined): OldColorValue => {
  switch (color) {
    case 'outlinePrimary':
      return 'primary'
    case 'outlineSecondary':
      return 'secondary'
    case 'secondary':
    case 'ghostSecondary':
      return 'outlineSecondary'
    default:
      return 'outlinePrimary'
  }
}

export const HeroCenter: React.FC<Page['hero']> = ({
  links,
  media,
  richText,
  heroCenter,
  includePreviewVideo,
  videoEmbedUrl,
  previewVideoLabel,
}) => {
  const linkCount = Array.isArray(links) ? links.length : 0
  const hasLinks = linkCount > 0
  const showPreview = Boolean(includePreviewVideo && videoEmbedUrl)
  const previewButtonLabel = previewVideoLabel || 'Watch preview'
  const heroButtonColor = heroCenter?.heroButtonColor || 'primary'
  const previewButtonColor = hasLinks
    ? getAlternateButtonColor(getLinkColor(linkCount - 1, heroButtonColor))
    : heroButtonColor
  const previewMapped = mapColorToButtonProps(previewButtonColor)
  const showButtonIcon = heroCenter?.buttonIcon !== 'none'
  const buttonCustomIcon =
    heroCenter?.buttonIcon === 'custom' ? (
      <CustomIcon icon={heroCenter?.buttonIconCustom as IconName} />
    ) : undefined

  return (
    <RevealFade className="relative wrapper mt-4 flex items-center justify-center rounded-2xl py-20">
      <RevealSection stagger className="relative z-20 md:max-w-2xl">
        <div className="mx-auto w-fit">
          {heroCenter?.kicker && (
            <Kicker tone={heroCenter.heroThemeColor === 'accent' ? 'inverse' : 'default'}>
              {heroCenter.kicker}
            </Kicker>
          )}
        </div>
        {heroCenter?.mainHeading && (
          <h1
            className={cn(
              heroCenter?.heroThemeColor === 'accent' ? 'text-background' : 'text-foreground',
              'mt-2 text-center font-family-display text-5xl md:text-6xl',
            )}
          >
            {heroCenter.textToHighlight
              ? heroCenter.mainHeading
                  .split(heroCenter.textToHighlight)
                  .map((part, index, array) => (
                    <Fragment key={index}>
                      <span>{part}</span>
                      {index < array.length - 1 && (
                        <span
                          className={getHeadingColor(
                            heroCenter?.heroThemeColor || 'accent-foreground-4',
                          )}
                        >
                          {heroCenter.textToHighlight}
                        </span>
                      )}
                    </Fragment>
                  ))
              : heroCenter.mainHeading}
          </h1>
        )}
        {richText && (
          <RichText
            className={cn(
              'mx-2 my-4 text-center text-balance md:m-6',
              heroCenter?.heroThemeColor === 'accent'
                ? 'text-neutral-200'
                : 'text-muted-foreground',
            )}
            data={richText}
            enableGutter={false}
          />
        )}
        {(hasLinks || showPreview) && (
          <ul className="flex flex-wrap justify-center gap-4">
            {links?.map(({ link }, i) => {
              return (
                <li key={i}>
                  <RenderLink
                    link={link}
                    color={getLinkColor(i, heroButtonColor)}
                    icon={showButtonIcon}
                    customIcon={buttonCustomIcon}
                  />
                </li>
              )
            })}
            {showPreview && videoEmbedUrl && (
              <li>
                <PreviewVideoButton
                  label={previewButtonLabel}
                  videoTitle={heroCenter?.mainHeading || previewButtonLabel}
                  videoEmbedUrl={videoEmbedUrl}
                  variant={previewMapped.variant}
                  color={previewMapped.color}
                  ghost={previewMapped.ghost}
                  icon={showButtonIcon}
                  customIcon={buttonCustomIcon}
                />
              </li>
            )}
          </ul>
        )}
      </RevealSection>
      <div
        className={cn(
          'pointer-events-none absolute top-0 left-0 z-0 block size-full overflow-hidden rounded-2xl',
        )}
      >
        {media && typeof media === 'object' && (
          <Media
            loading="eager"
            priority
            resource={media}
            htmlelement={null}
            pictureClassName="visible absolute inset-0 block size-full overflow-hidden object-cover opacity-100 transition-all duration-500 ease-in"
            imgClassName="size-full object-cover object-center"
          />
        )}
      </div>
      <div
        className={cn(
          getHeroThemeColor(heroCenter?.heroThemeColor || 'accent-foreground-4'),
          'absolute top-0 left-2 -z-1 h-full w-[calc(100%-1rem)] rounded-2xl transition-all duration-300 ease-in-out lg:left-0 lg:w-full',
        )}
      />
    </RevealFade>
  )
}
